import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Typography,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { useNavigate, useLocation } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CategoryIcon from '@mui/icons-material/Category';
import { sidebarStructure } from '../../navigations/sidebarConfig';
import { IMPORT_BUSINESS_LIST_ACCESS } from '../../pages/importBusinesses/importBusinessPermissions';
import { LEASE_LIST_ACCESS } from '../../pages/leases/leasePermissions';
import { MERCHANT_SERVICE_PROVIDER_LIST_ACCESS } from '../../pages/merchantServiceProviders/merchantServiceProviderPermissions';
import { EQUIPMENT_MODEL_LIST_ACCESS } from '../../pages/equipmentModels/equipmentModelPermissions';
import { useAuth } from '../../hooks/useAuth';
import { useSidebarRail } from '../../context/SidebarRailContext';

const normalizePerm = (p) =>
  (p || '').toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
const multiPermMap = {
  '/users-list': ['user-list', 'user-direct-permission'],
  '/permissions/assign': ['role-view', 'user-direct-permission'],
  '/data': IMPORT_BUSINESS_LIST_ACCESS,
  '/leases': LEASE_LIST_ACCESS,
  '/merchant-service-providers': MERCHANT_SERVICE_PROVIDER_LIST_ACCESS,
  '/equipment-models': EQUIPMENT_MODEL_LIST_ACCESS,
  '/customers': [
    'customer-list',
    'customer-create',
    'customer-view',
    'customer-edit',
    'customer-delete',
  ],
};

const Sidebar = () => {
  const theme = useTheme();
  const brandGradient = theme.brand?.gradient ?? `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`;
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const { permissions, impersonating } = useAuth();
  const sidebarTopPx = impersonating ? 73 + 40 : 73;
  const navigate = useNavigate();
  const location = useLocation();
  const [activeKey, setActiveKey] = useState(location.pathname);
  const [openGroups, setOpenGroups] = useState({});
  /** sm+: collapsed/expanded width; must use context so PageLayout main column margin stays in sync. */
  const { railExpanded, setRailExpanded } = useSidebarRail();

  useEffect(() => {
    setActiveKey(location.pathname);
  }, [location.pathname]);

  const hasPerm = (perm) =>
    permissions.some((p) => normalizePerm(p) === normalizePerm(perm));
  const hasAnyPerm = (perms) =>
    perms.some((p) => permissions.some((u) => normalizePerm(u) === normalizePerm(p)));

  const isItemVisible = (item) => {
    if (hasPerm('super-admin')) return true;
    const multiPerms = multiPermMap[item.path];
    if (multiPerms) return hasAnyPerm(multiPerms);
    if (!item.permission) return true;
    return hasPerm(item.permission);
  };

  const toggleGroup = (label) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isGroupOpen = (label, children) => {
    if (openGroups[label] !== undefined) return openGroups[label];
    return children.some((c) => c.path === activeKey);
  };

  const listContent = useMemo(() => {
    const nodes = [];
    sidebarStructure.forEach((entry) => {
      if (entry.type === 'item') {
        if (!isItemVisible(entry)) return;
        nodes.push({ type: 'item', key: entry.path, ...entry });
        return;
      }
      if (entry.type === 'group') {
        const visibleChildren = entry.children.filter((c) => isItemVisible(c));
        if (visibleChildren.length === 0) return;
        nodes.push({ type: 'group', key: entry.label, label: entry.label, icon: entry.icon, children: visibleChildren });
      }
    });
    return nodes;
  }, [permissions]);

  const handleItemClick = (path) => {
    setActiveKey(path);
    navigate(path);
  };

  const linkButtonSx = (path) => ({
    px: 2,
    py: 1.5,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    bgcolor: activeKey === path ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
    border: '1px solid',
    borderColor: activeKey === path ? 'primary.main' : 'divider',
    borderLeft: activeKey === path ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
    '&:hover': {
      bgcolor: activeKey === path ? alpha(theme.palette.primary.main, 0.1) : 'action.hover',
    },
    transition: 'all 0.2s ease',
  });

  /** sm+: icon rail (72px); `.sidebar-expanded` = 250px with labels. xs: fixed width with labels. */
  const sidebarRailSx = {
    width: { xs: 200, sm: 72 },
    transition: 'width 0.22s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.22s ease',
    '& .sidebar-label': {
      transition: 'opacity 0.18s ease, max-width 0.22s ease',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    '& .sidebar-footer-text': {
      transition: 'opacity 0.18s ease, max-height 0.22s ease',
    },
    '@media (min-width: 600px)': {
      '&.sidebar-expanded': {
        width: 250,
      },
      '& .sidebar-label': {
        opacity: 0,
        maxWidth: 0,
      },
      '&.sidebar-expanded .sidebar-label': {
        opacity: 1,
        maxWidth: 220,
      },
      '& .sidebar-item-btn, & .sidebar-group-btn': {
        justifyContent: 'center',
        gap: 0,
      },
      '&.sidebar-expanded .sidebar-item-btn': {
        justifyContent: 'flex-start',
        gap: 1.5,
      },
      '&.sidebar-expanded .sidebar-group-btn': {
        justifyContent: 'space-between',
        gap: 1,
      },
      '& .sidebar-footer-text': {
        opacity: 0,
        maxHeight: 0,
        overflow: 'hidden',
      },
      '&.sidebar-expanded .sidebar-footer-text': {
        opacity: 1,
        maxHeight: 56,
      },
    },
  };

  const railOpen = isSmUp && railExpanded;

  return (
    <Box
      className={railOpen ? 'sidebar-expanded' : undefined}
      sx={{
        ...sidebarRailSx,
        height: `calc(100vh - ${sidebarTopPx}px)`,
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
        overflow: 'hidden',
        position: 'fixed',
        top: sidebarTopPx,
        left: 0,
        zIndex: 1100,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {isSmUp && (
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: railExpanded ? 'flex-end' : 'center',
            px: 0.5,
            py: 0.5,
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 44,
          }}
        >
          <IconButton
            size="small"
            aria-expanded={railExpanded}
            aria-label={railExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
            onClick={() => setRailExpanded((v) => !v)}
            sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
          >
            {railExpanded ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1.5,
          py: 1,
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: alpha(theme.palette.text.secondary, 0.35), borderRadius: '3px' },
          '&::-webkit-scrollbar-thumb:hover': { background: alpha(theme.palette.text.secondary, 0.55) },
        }}
      >
        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {listContent.map((node) => {
            if (node.type === 'item') {
              return (
                <ListItem key={node.key} disablePadding sx={{ mt: 0 }}>
                  <ListItemButton
                    className="sidebar-item-btn"
                    onClick={() => handleItemClick(node.path)}
                    sx={{
                      ...linkButtonSx(node.path),
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: activeKey === node.path ? alpha(theme.palette.primary.main, 0.1) : 'action.hover',
                      },
                    }}
                  >
                    <Box sx={{ color: activeKey === node.path ? 'primary.main' : 'text.secondary', flexShrink: 0 }}>{node.icon}</Box>
                    <Typography
                      component="span"
                      className="sidebar-label"
                      variant="body2"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: activeKey === node.path ? 600 : 500,
                        color: activeKey === node.path ? 'primary.main' : 'text.primary',
                      }}
                    >
                      {node.label}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              );
            }
            const open = isGroupOpen(node.label, node.children);
            return (
              <Box key={node.key} sx={{ mt: 0.5 }}>
                <ListItem disablePadding>
                  <ListItemButton
                    className="sidebar-group-btn"
                    onClick={() => toggleGroup(node.label)}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: 'background.paper',
                      color: 'text.primary',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        background: brandGradient,
                        color: 'white',
                        borderColor: 'transparent',
                        '& .group-icon': { color: 'white' },
                        '& .group-chevron': { color: 'white' },
                      },
                      ...(open && {
                        background: brandGradient,
                        color: 'white',
                        borderColor: 'transparent',
                        '& .group-icon': { color: 'white' },
                        '& .group-chevron': { color: 'white' },
                      }),
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                      <Box className="group-icon" sx={{ display: 'flex', alignItems: 'center', color: open ? 'white' : 'text.secondary', flexShrink: 0 }}>
                        {node.icon ?? <CategoryIcon sx={{ fontSize: 20 }} />}
                      </Box>
                      <Typography component="span" className="sidebar-label" variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'inherit' }}>
                        {node.label}
                      </Typography>
                    </Box>
                    <Box className="group-chevron sidebar-label" component="span" sx={{ color: open ? 'white' : 'text.secondary', flexShrink: 0, display: 'flex' }}>
                      {open ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
                    </Box>
                  </ListItemButton>
                </ListItem>
                {open && (
                  <Box sx={{ pl: 1.5, pt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {node.children.map((child) => (
                      <ListItem key={child.path} disablePadding>
                        <ListItemButton
                          className="sidebar-item-btn"
                          onClick={() => handleItemClick(child.path)}
                          sx={{
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            py: 1.25,
                            pl: 2,
                            pr: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 2px rgba(15,23,42,0.04)',
                            borderLeft: activeKey === child.path ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
                            '&:hover': { bgcolor: 'action.hover', borderColor: alpha(theme.palette.primary.main, 0.35) },
                          }}
                        >
                          <Box sx={{ color: activeKey === child.path ? 'primary.main' : 'text.secondary', flexShrink: 0 }}>{child.icon}</Box>
                          <Typography
                            component="span"
                            className="sidebar-label"
                            variant="body2"
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: activeKey === child.path ? 600 : 500,
                              color: activeKey === child.path ? 'primary.main' : 'text.primary',
                            }}
                          >
                            {child.label}
                          </Typography>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </List>
      </Box>

      <Box
        className="sidebar-footer-text"
        sx={{
          px: 2,
          py: 3,
          color: 'text.secondary',
          fontSize: '0.75rem',
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption">Powered by</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
            INOVIOTECH INC
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Sidebar;
